import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiCreditCard, FiLoader, FiAlertCircle, FiCheckCircle, FiArrowLeft } from 'react-icons/fi';
import { meusCartoesAPI, clienteSobreAPI } from '../services/api';
import Layout from '../components/Layout';
import theme from '../styles/theme';

const Container = styled.div`
  padding: ${theme.spacing[6]};
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[8]};
`;

const BackButton = styled.button`
  padding: ${theme.spacing[2]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${theme.colors.background.secondary};
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['3xl']};
  font-weight: 700;
  margin: 0;
`;

const Card = styled.div`
  background: ${theme.colors.background.card};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  box-shadow: ${theme.shadows.md};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Required = styled.span`
  color: ${theme.colors.error[500]};
  margin-left: ${theme.spacing[1]};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Button = styled.button`
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  background: ${props => props.variant === 'secondary' ? 'transparent' : theme.colors.primary[500]};
  border: 1px solid ${props => props.variant === 'secondary' ? theme.colors.border.primary : theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.lg};
  color: ${props => props.variant === 'secondary' ? theme.colors.text.primary : theme.colors.white};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${props => props.variant === 'secondary' ? theme.colors.background.primary : theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  margin-top: ${theme.spacing[4]};
`;

const ErrorMessage = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.error[500]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const SuccessMessage = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.success[500]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]};
  gap: ${theme.spacing[4]};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${theme.colors.border.primary};
  border-top: 3px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const CartaoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  
  const [formData, setFormData] = useState({
    cliente_id: '',
    holder_name: '',
    number: '',
    ccv: '',
    expiry_month: '',
    expiry_year: '',
    holder_cpf_cnpj: '',
    holder_email: '',
    holder_mobile_phone: '',
    holder_postal_code: '',
    holder_address: '',
    holder_address_number: '',
    holder_address_complement: '',
    padrao: !isEdit // true para novo cartão, false para edição (será atualizado quando carregar)
  });

  useEffect(() => {
    carregarClientes();
    if (isEdit) {
      carregarCartao();
    }
  }, [id]);

  const carregarClientes = async () => {
    try {
      setLoadingClientes(true);
      const response = await clienteSobreAPI.listarClientes();
      if (response.data?.success) {
        const clientesLista = response.data.data || [];
        setClientes(clientesLista);
        // Se houver apenas um cliente e não estiver editando, selecionar automaticamente
        if (!isEdit && clientesLista.length === 1) {
          setFormData(prev => ({
            ...prev,
            cliente_id: String(clientesLista[0].id)
          }));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar clientes', error);
    } finally {
      setLoadingClientes(false);
    }
  };

  const carregarCartao = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await meusCartoesAPI.buscar(id);
      
      if (response.data.success) {
        const cartao = response.data.data;
        setFormData({
          cliente_id: cartao.cliente_id ? String(cartao.cliente_id) : '',
          holder_name: cartao.holder_name || '',
          number: '', // Não carregar número completo por segurança
          ccv: '', // Não carregar CCV por segurança
          expiry_month: cartao.expiry_month || '',
          expiry_year: cartao.expiry_year || '',
          holder_cpf_cnpj: cartao.holder_cpf_cnpj || '',
          holder_email: cartao.holder_email || '',
          holder_mobile_phone: cartao.holder_mobile_phone || '',
          holder_postal_code: cartao.holder_postal_code || '',
          holder_address: cartao.holder_address || '',
          holder_address_number: cartao.holder_address_number || '',
          holder_address_complement: cartao.holder_address_complement || '',
          padrao: cartao.padrao || false
        });
      } else {
        setError(response.data.message || 'Erro ao carregar cartão');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar cartão');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const aplicarMascaraCPF = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '');
    
    if (apenasNumeros.length <= 11) {
      return apenasNumeros
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    
    if (apenasNumeros.length <= 14) {
      return apenasNumeros
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }
    
    return apenasNumeros.slice(0, 14)
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };

  const aplicarMascaraTelefone = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '');
    
    if (apenasNumeros.length <= 10) {
      return apenasNumeros
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    
    if (apenasNumeros.length <= 11) {
      return apenasNumeros
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2');
    }
    
    return apenasNumeros.slice(0, 11)
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  };

  const aplicarMascaraCEP = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '');
    
    if (apenasNumeros.length <= 8) {
      return apenasNumeros.replace(/(\d{5})(\d)/, '$1-$2');
    }
    
    return apenasNumeros.slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2');
  };

  const aplicarMascaraNumeroCartao = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '');
    const numerosLimitados = apenasNumeros.slice(0, 19);
    return numerosLimitados.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatarNomeTitular = (valor) => {
    return valor.replace(/[0-9]/g, '').replace(/[^a-zA-ZÀ-ÿ\s]/g, '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validações
    if (isEdit) {
      // Edição: apenas validar campos obrigatórios que podem ser editados
      if (!formData.holder_name) {
        setError('Nome do titular é obrigatório');
        return;
      }
    } else {
      // Novo cartão: validar todos os campos obrigatórios
      if (clientes.length > 1 && !formData.cliente_id) {
        setError('Selecione um cliente');
        return;
      }
      
      if (!formData.holder_name || !formData.number || !formData.ccv || !formData.expiry_month || 
          !formData.expiry_year || !formData.holder_cpf_cnpj) {
        setError('Preencha todos os campos obrigatórios');
        return;
      }

      const nomeLimpo = formData.holder_name.trim();
      const partesNome = nomeLimpo.split(/\s+/).filter(parte => parte.length > 0);
      if (partesNome.length < 2) {
        setError('O nome do titular deve conter pelo menos nome e sobrenome');
        return;
      }

      const cardNumberClean = formData.number.replace(/\D/g, '');
      if (cardNumberClean.length < 13 || cardNumberClean.length > 19) {
        setError('Número do cartão inválido');
        return;
      }

      const ccvClean = formData.ccv.replace(/\D/g, '');
      if (ccvClean.length < 3 || ccvClean.length > 4) {
        setError('CCV inválido (deve ter 3 ou 4 dígitos)');
        return;
      }

      if (!formData.expiry_month || !formData.expiry_year) {
        setError('Selecione o mês e ano de validade');
        return;
      }

      if (!formData.holder_email || !formData.holder_email.trim()) {
        setError('Email do titular é obrigatório');
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.holder_email.trim())) {
        setError('Email inválido');
        return;
      }

      const telefoneClean = formData.holder_mobile_phone.replace(/\D/g, '');
      if (!telefoneClean || telefoneClean.length !== 11) {
        setError('Telefone celular inválido (deve ter 11 dígitos)');
        return;
      }

      const cepClean = formData.holder_postal_code.replace(/\D/g, '');
      if (!cepClean || cepClean.length !== 8) {
        setError('CEP inválido (deve ter 8 dígitos)');
        return;
      }

      if (!formData.holder_address || !formData.holder_address.trim()) {
        setError('Endereço é obrigatório');
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);
      
      let response;
      if (isEdit) {
        // Atualizar apenas campos editáveis
        response = await meusCartoesAPI.atualizar(id, {
          holder_name: formData.holder_name,
          expiry_month: formData.expiry_month,
          expiry_year: formData.expiry_year,
          holder_email: formData.holder_email,
          holder_mobile_phone: formData.holder_mobile_phone,
          holder_postal_code: formData.holder_postal_code,
          holder_address: formData.holder_address,
          holder_address_number: formData.holder_address_number,
          holder_address_complement: formData.holder_address_complement,
          padrao: formData.padrao
        });
      } else {
        // Preparar dados para criação, incluindo cliente_id se houver múltiplos clientes
        const dadosCriacao = { ...formData };
        // Se houver apenas um cliente, não precisa enviar cliente_id (API usa o primeiro automaticamente)
        if (clientes.length === 1 && !dadosCriacao.cliente_id) {
          dadosCriacao.cliente_id = String(clientes[0].id);
        }
        response = await meusCartoesAPI.criar(dadosCriacao);
      }
      
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/meus-cartoes');
        }, 2000);
      } else {
        setError(response.data.message || `Erro ao ${isEdit ? 'atualizar' : 'salvar'} cartão`);
      }
    } catch (err) {
      let errorMessage = `Erro ao ${isEdit ? 'atualizar' : 'salvar'} cartão`;
      
      if (err.response?.data) {
        if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
          errorMessage = err.response.data.errors
            .map(e => e.description || e.message || 'Erro desconhecido')
            .join('; ');
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Container>
          <LoadingContainer>
            <LoadingSpinner />
            <p>Carregando cartão...</p>
          </LoadingContainer>
        </Container>
      </Layout>
    );
  }

  if (success) {
    return (
      <Layout>
        <Container>
          <Card>
            <SuccessMessage>
              <FiCheckCircle />
              Cartão {isEdit ? 'atualizado' : 'cadastrado'} com sucesso! Redirecionando...
            </SuccessMessage>
          </Card>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Header>
          <BackButton onClick={() => navigate('/meus-cartoes')}>
            <FiArrowLeft />
          </BackButton>
          <Title>{isEdit ? 'Editar Cartão' : 'Adicionar Cartão'}</Title>
        </Header>

        <Card>
          {error && (
            <ErrorMessage>
              <FiAlertCircle />
              {error}
            </ErrorMessage>
          )}

          <Form onSubmit={handleSubmit}>
            <Grid>
              {!isEdit && clientes.length > 1 && (
                <FormGroup style={{ gridColumn: '1 / -1' }}>
                  <Label>
                    Cliente
                    <Required>*</Required>
                  </Label>
                  <Select
                    value={formData.cliente_id}
                    onChange={(e) => handleChange('cliente_id', e.target.value)}
                    disabled={saving || loadingClientes}
                    required
                  >
                    <option value="">Selecione um cliente</option>
                    {clientes.map((cliente) => (
                      <option key={cliente.id} value={cliente.id}>
                        {cliente.nome_fantasia || cliente.nome_social || `Cliente #${cliente.id}`}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
              )}

              <FormGroup>
                <Label>
                  Nome completo como está no cartão
                  <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  value={formData.holder_name}
                  onChange={(e) => handleChange('holder_name', formatarNomeTitular(e.target.value))}
                  disabled={saving}
                  placeholder="Nome completo"
                  required
                />
              </FormGroup>

              {!isEdit && (
                <>
                  <FormGroup>
                    <Label>
                      Número do Cartão
                      <Required>*</Required>
                    </Label>
                    <Input
                      type="text"
                      value={formData.number}
                      onChange={(e) => handleChange('number', aplicarMascaraNumeroCartao(e.target.value))}
                      disabled={saving}
                      placeholder="1234 5678 9012 3456"
                      maxLength={23}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Código de Segurança (CCV)
                      <Required>*</Required>
                    </Label>
                    <Input
                      type="text"
                      value={formData.ccv}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                        handleChange('ccv', value);
                      }}
                      disabled={saving}
                      placeholder="123"
                      maxLength={4}
                      required
                    />
                  </FormGroup>
                </>
              )}

              <FormGroup>
                <Label>
                  Mês de Validade
                  <Required>*</Required>
                </Label>
                <Select
                  value={formData.expiry_month}
                  onChange={(e) => handleChange('expiry_month', e.target.value)}
                  disabled={saving}
                  required
                >
                  <option value="">Selecione o mês</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <option key={month} value={month.toString().padStart(2, '0')}>
                      {month.toString().padStart(2, '0')}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>
                  Ano de Validade
                  <Required>*</Required>
                </Label>
                <Select
                  value={formData.expiry_year}
                  onChange={(e) => handleChange('expiry_year', e.target.value)}
                  disabled={saving}
                  required
                >
                  <option value="">Selecione o ano</option>
                  {Array.from({ length: 16 }, (_, i) => {
                    const year = new Date().getFullYear() + i;
                    return (
                      <option key={year} value={year.toString()}>
                        {year}
                      </option>
                    );
                  })}
                </Select>
              </FormGroup>

              {!isEdit && (
                <>
                  <FormGroup>
                    <Label>
                      CPF/CNPJ do Titular
                      <Required>*</Required>
                    </Label>
                    <Input
                      type="text"
                      value={formData.holder_cpf_cnpj}
                      onChange={(e) => handleChange('holder_cpf_cnpj', aplicarMascaraCPF(e.target.value))}
                      disabled={saving}
                      placeholder="000.000.000-00"
                      required
                    />
                  </FormGroup>
                </>
              )}

              <FormGroup>
                <Label>
                  Email do Titular
                  {!isEdit && <Required>*</Required>}
                </Label>
                <Input
                  type="email"
                  value={formData.holder_email}
                  onChange={(e) => handleChange('holder_email', e.target.value)}
                  disabled={saving}
                  placeholder="email@exemplo.com"
                  required={!isEdit}
                />
              </FormGroup>

              {!isEdit && (
                <>
                  <FormGroup>
                    <Label>
                      Telefone Celular
                      <Required>*</Required>
                    </Label>
                    <Input
                      type="text"
                      value={formData.holder_mobile_phone}
                      onChange={(e) => handleChange('holder_mobile_phone', aplicarMascaraTelefone(e.target.value))}
                      disabled={saving}
                      placeholder="(00) 00000-0000"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      CEP
                      <Required>*</Required>
                    </Label>
                    <Input
                      type="text"
                      value={formData.holder_postal_code}
                      onChange={(e) => handleChange('holder_postal_code', aplicarMascaraCEP(e.target.value))}
                      disabled={saving}
                      placeholder="00000-000"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Endereço
                      <Required>*</Required>
                    </Label>
                    <Input
                      type="text"
                      value={formData.holder_address}
                      onChange={(e) => handleChange('holder_address', e.target.value)}
                      disabled={saving}
                      placeholder="Rua, Avenida, etc."
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Número</Label>
                    <Input
                      type="text"
                      value={formData.holder_address_number}
                      onChange={(e) => handleChange('holder_address_number', e.target.value)}
                      disabled={saving}
                      placeholder="123"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Complemento</Label>
                    <Input
                      type="text"
                      value={formData.holder_address_complement}
                      onChange={(e) => handleChange('holder_address_complement', e.target.value)}
                      disabled={saving}
                      placeholder="Apto, Sala, etc."
                    />
                  </FormGroup>
                </>
              )}

              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    checked={formData.padrao}
                    onChange={(e) => handleChange('padrao', e.target.checked)}
                    disabled={saving}
                  />
                  <span style={{ marginLeft: theme.spacing[2] }}>Definir como cartão padrão</span>
                </Label>
              </FormGroup>
            </Grid>

            <ButtonGroup>
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <FiLoader style={{ animation: 'spin 1s linear infinite' }} />
                    {isEdit ? 'Atualizando...' : 'Salvando...'}
                  </>
                ) : (
                  <>
                    <FiCreditCard />
                    {isEdit ? 'Atualizar Cartão' : 'Salvar Cartão'}
                  </>
                )}
              </Button>
              <Button 
                type="button" 
                variant="secondary" 
                onClick={() => navigate('/meus-cartoes')}
                disabled={saving}
              >
                Cancelar
              </Button>
            </ButtonGroup>
          </Form>
        </Card>
      </Container>
    </Layout>
  );
};

export default CartaoForm;

